import { DatePicker, Select, Segmented, Space } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { UploadRecordSource } from '@/api/uploadRecords';
import { getSourceLabels, sourcesForDeliverableType, type DeliverableType } from '@/constants/upload';
import { getPeriodOptions, type Period } from './uploadStatsConfig';
import { useT } from '@/i18n';

const { RangePicker } = DatePicker;

interface Props {
  board: DeliverableType;
  period: Period;
  onPeriodChange: (period: Period) => void;
  range: [Dayjs, Dayjs] | null;
  onRangeChange: (range: [Dayjs, Dayjs] | null) => void;
  projectId?: number;
  onProjectChange: (projectId?: number) => void;
  source?: UploadRecordSource;
  onSourceChange: (source?: UploadRecordSource) => void;
  projects: { id: number; name: string }[];
}

const UploadStatsFilters = ({
  board,
  period,
  onPeriodChange,
  range,
  onRangeChange,
  projectId,
  onProjectChange,
  source,
  onSourceChange,
  projects,
}: Props) => {
  const { t } = useT();
  const sourceLabels = getSourceLabels(t);
  return (
  <Space wrap size="middle">
    <Segmented
      value={period}
      onChange={(value) => onPeriodChange(value as Period)}
      options={getPeriodOptions(t)}
    />
    {period === 'custom' && (
      <RangePicker
        value={range}
        onChange={(value) => onRangeChange(value as [Dayjs, Dayjs] | null)}
        allowClear={false}
        presets={[
          { label: t('近7天'), value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().startOf('day')] },
          { label: t('近30天'), value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().startOf('day')] },
          { label: t('本周'), value: [dayjs().subtract((dayjs().day() + 6) % 7, 'day').startOf('day'), dayjs().startOf('day')] },
          { label: t('本月'), value: [dayjs().startOf('month'), dayjs().startOf('day')] },
        ]}
      />
    )}
    <Select
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={t('全部项目')}
      style={{ width: 200 }}
      value={projectId}
      onChange={(value) => onProjectChange(value)}
      options={projects.map((project) => ({ label: project.name, value: project.id }))}
    />
    <Select
      allowClear
      placeholder={t('全部来源')}
      style={{ width: 160 }}
      value={source}
      onChange={(value) => onSourceChange(value)}
      options={sourcesForDeliverableType(board).map((value) => ({
        value,
        label: sourceLabels[value]?.label ?? value,
      }))}
    />
  </Space>
  );
};

export default UploadStatsFilters;
